import {
    FunctionComponent,
    ReactNode,
    useEffect,
    useState
} from 'react';
import {
    AuthUser,
    fetchAuthSession,
    getCurrentUser,
    JWT
} from 'aws-amplify/auth';
import type {AuthStatus} from '@flexinet/auth-components';
import {AuthContext} from '../context/AuthContext';


interface IAuthProvider {
    children?: ReactNode
}

export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {

    const [user, setUser] = useState<AuthUser | undefined>();
    const [idToken, setIdToken] = useState<JWT | undefined>();
    const [accessToken, setAccessToken] = useState<JWT | undefined>();
    const [cognitoGroups, setCognitoGroups] = useState<string[] | undefined>();
    const [role, setRole] = useState<string | undefined>();
    // FNP-1728: both lookups must settle before the session counts as resolved.
    const [userOutcome, setUserOutcome] = useState<'authenticated' | 'anonymous' | undefined>();
    const [sessionSettled, setSessionSettled] = useState(false);
    const status: AuthStatus = userOutcome === undefined || !sessionSettled ? 'resolving' : userOutcome;


    useEffect(() => {
        const fetchCognitoUser = async () => {

            try {
                const result = await getCurrentUser()
                // console.debug('AUTH CONTEXT. FETCH USER', result)

                const {
                    username,
                    userId,
                    signInDetails
                } = result

                setUser({
                            username,
                            userId,
                            signInDetails
                        })
                setUserOutcome('authenticated')


            } catch (error) {
                // Amplify throws here when nobody is signed in, so this is how
                // an ordinary signed-out visit resolves.
                setUserOutcome('anonymous')
                console.trace(
                    'AUTH CONTEXT. FETCH USER ERROR',
                    error
                );
            }
        };

        fetchCognitoUser();

    }, []);


    useEffect(() => {
        const fetchSession = async () => {

            try {
                const {
                    accessToken,
                    idToken
                } = (await fetchAuthSession()).tokens ?? {};

                setIdToken(idToken);
                setAccessToken(accessToken);
                if (typeof idToken !== 'undefined'
                    && idToken.payload['cognito:groups'] !== undefined
                    && Array.isArray(idToken.payload['cognito:groups'])
                    && idToken.payload['cognito:groups'].length > 0) {
                    setCognitoGroups(idToken.payload['cognito:groups'] as string[]);
                }

            } catch (error) {
                console.trace(
                    'AUTH CONTEXT. FETCH SESSION ERROR',
                    error
                );
            } finally {
                setSessionSettled(true);
            }

        };

        fetchSession();
    }, []);

    useEffect(() => {

        if (typeof idToken !== 'undefined'
            && idToken.payload['cognito:groups'] !== undefined
            && Array.isArray(idToken.payload['cognito:groups'])
            && idToken.payload['cognito:groups'].length > 0) {


            const adminItem = (idToken.payload['cognito:groups'] as string[]).find((item: string) => item === 'admin')
            if (adminItem) {
                setRole('Admin')
            }

        }

    }, [idToken])


    return (
        <AuthContext.Provider value={{
            user,
            status,
            idToken,
            accessToken,
            cognitoGroups,
            role
        }}>
            {children}
        </AuthContext.Provider>
    );
};
